import type React from "react";
import {useRef} from "react";

import {useFrame} from "@react-three/fiber";
import {type GameObjectType, ShapeType, config} from "@repo/shared";
import {Matrix4, type Mesh, Vector3} from "three";

import {useSelection} from "../useSelection";
import {DraggableTransform} from "./drag/DraggableTransform";
import {useButtonScale} from "./useButtonScale";

const LineHandles: React.FC = () => {
  const {buttonScale} = useButtonScale();
  const {applyTransformToSelected, recalculateBoundingBox, selectedObject} =
    useSelection();
  const buttonRefs = [useRef<Mesh>(null), useRef<Mesh>(null)]; // Two buttons

  const handleScaleTransform = (deltaMatrix: Matrix4, index: number) => {
    if (!selectedObject.current) return;

    // Get the position of the other button
    const otherIndex = index === 0 ? 1 : 0;
    const otherButtonPosition = buttonRefs[otherIndex]?.current?.position;
    const otherButtonMatrix = buttonRefs[otherIndex]?.current?.matrix;

    if (!otherButtonPosition || !otherButtonMatrix) return;

    // Get the position of the current button
    const currentButtonPosition = buttonRefs[index]?.current?.position;
    if (!currentButtonPosition) return;

    // Calculate the transformed position of the current button
    const newCurrentButtonPosition = currentButtonPosition
      .clone()
      .applyMatrix4(deltaMatrix);

    // Vectors from the other button to the current button (before and after the delta)
    const vec1 = new Vector3()
      .subVectors(currentButtonPosition, otherButtonPosition)
      .normalize();
    const vec2 = new Vector3()
      .subVectors(newCurrentButtonPosition, otherButtonPosition)
      .normalize();

    // Ensure vectors are valid (Removed, because it's not working when both anchors are at the same position)
    // if (vec1.lengthSq() === 0 || vec2.lengthSq() === 0) return;

    // Calculate the dot product and clamp it to avoid NaN errors
    const dot = Math.min(Math.max(vec1.dot(vec2), -1), 1);

    // Calculate the angle between the two vectors
    const angle = Math.acos(dot); // Returns angle in radians

    // Determine the rotation direction (cross product)
    const crossZ = vec1.x * vec2.y - vec1.y * vec2.x; // Z component of the cross product
    const signedAngle = crossZ < 0 ? -angle : angle; // Negative for clockwise, positive for counter-clockwise

    // Create the rotation matrix
    const rotationMatrix = new Matrix4().makeRotationZ(signedAngle);

    // Calculate the original and new distances between the buttons
    const minScale = Math.min(
      selectedObject.current.scale.x,
      selectedObject.current.scale.y,
    );
    const originalDistance =
      currentButtonPosition.distanceTo(otherButtonPosition) + minScale;
    const newDistance =
      newCurrentButtonPosition.distanceTo(otherButtonPosition) + minScale;

    // Calculate the scaling factor based on the change in distance
    const scaleFactor = newDistance / originalDistance;

    // Determine scaling matrix (scale only along the larger axis)
    const scaleMatrix = new Matrix4();
    const largerAxis =
      Math.abs(selectedObject.current.scale.x) >
      Math.abs(selectedObject.current.scale.y)
        ? "x"
        : "y";
    if (largerAxis === "x") {
      scaleMatrix.makeScale(scaleFactor, 1, 1); // Scale along X-axis
    } else {
      scaleMatrix.makeScale(1, scaleFactor, 1); // Scale along Y-axis
    }

    const otherButtonAnchor = otherButtonMatrix.clone();

    const direction = otherButtonPosition
      .clone()
      .sub(currentButtonPosition)
      .normalize();

    // Scale the direction vector by the radius
    const offset = direction.multiplyScalar(minScale * 0.5);
    const outerAnchor = otherButtonPosition.clone().add(offset);
    const outerTranslation = new Matrix4().makeTranslation(outerAnchor);
    const rotation = new Matrix4().makeRotationFromEuler(
      selectedObject.current.rotation,
    );
    const merged = outerTranslation.multiply(rotation);

    applyTransformToSelected([
      merged.clone().invert(),
      scaleMatrix,
      merged,
      otherButtonAnchor.clone().invert(),
      rotationMatrix,
      otherButtonAnchor.clone(),
    ]);
  };

  useFrame(() => {
    if (
      selectedObject.current &&
      config.objectTypes[
        selectedObject.current.userData["type"] as GameObjectType
      ].shape === ShapeType.RECTANGLE
    ) {
      // Get the scales
      const scaleX = selectedObject.current.scale.x;
      const scaleY = selectedObject.current.scale.y;

      // Determine the offset: inward along the longer axis
      const isXLonger = scaleX >= scaleY;
      const max = Math.max(scaleX, scaleY);
      const min = Math.min(scaleX, scaleY);
      const fac = (1 - min / max) * 0.5;

      // Calculate positions for the buttons
      const p1 = new Vector3(isXLonger ? fac : 0, isXLonger ? 0 : fac, 50);
      const p2 = new Vector3(isXLonger ? -fac : 0, isXLonger ? 0 : -fac, 50);

      // Transform positions by the selectedObject.current matrix
      p1.applyMatrix4(selectedObject.current.matrix);
      p2.applyMatrix4(selectedObject.current.matrix);

      // Compute button positions inward along the longer axis
      const buttonPositions = [p1, p2];

      // Update button positions
      buttonRefs.forEach((ref, index) => {
        if (ref.current) {
          ref.current.position.copy(buttonPositions[index]!);
          ref.current.scale.set(buttonScale.current, buttonScale.current, 1);
        }
      });
    } else {
      // Hide buttons when no valid selectedObject.current is available
      buttonRefs.forEach((ref) => {
        if (ref.current) {
          ref.current.scale.set(0, 0, 0);
        }
      });
    }
  });

  return (
    <>
      {buttonRefs.map((ref, index) => (
        <DraggableTransform
          key={index}
          onTransform={(matrix) => handleScaleTransform(matrix, index)}
          onEnd={recalculateBoundingBox}
        >
          <mesh ref={ref}>
            <circleGeometry args={[0.55, 32]} />
            <meshBasicMaterial color="black" />
            <mesh>
              <circleGeometry args={[0.5, 32]} />
              <meshBasicMaterial color="yellow" />
            </mesh>
          </mesh>
        </DraggableTransform>
      ))}
    </>
  );
};

export default LineHandles;
